import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'
import MDButton from 'components/MDButton'

const OTP_LENGTH = 6

// Reusable OTP modal. Used in two places:
//   1. The 2nd step of admin login (after email/password succeeds)
//   2. The sensitive-action gate (edit/delete/booking buttons/settings)
//
// The parent owns the verification logic via `onVerify` so the same modal
// works for either flow. `onResend` is optional — when provided, a Resend
// button is shown.
function OtpVerifyModal({
  open,
  email,
  title,
  description,
  busy,
  error,
  onVerify,
  onResend,
  onClose,
  expiresAt,
}) {
  const [otp, setOtp] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(null)
  const inputRef = useRef(null)

  // Reset local state every time the modal is re-opened so a stale OTP
  // from a previous attempt doesn't linger.
  useEffect(() => {
    if (open) {
      setOtp('')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Countdown driven by server-issued expiry.
  useEffect(() => {
    if (!open || !expiresAt) {
      setSecondsLeft(null)
      return undefined
    }
    const target = new Date(expiresAt).getTime()
    const tick = () => {
      const diff = Math.max(0, Math.floor((target - Date.now()) / 1000))
      setSecondsLeft(diff)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [open, expiresAt])

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (busy) return
    if (otp.length !== OTP_LENGTH) return
    onVerify?.(otp)
  }

  const expired = secondsLeft === 0

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      // Prevent dismiss-on-backdrop while a verification is in flight so
      // we don't drop the user mid-request.
      disableEscapeKeyDown={busy}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title || 'Verify OTP'}
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit} role="form" mt={1}>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            {description ||
              `We sent a 6-digit code to ${email || 'your email'}. Enter it below to continue.`}
          </MDTypography>

          <MDInput
            inputRef={inputRef}
            type="text"
            label="6-digit code"
            value={otp}
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)
              setOtp(next)
            }}
            inputProps={{
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              maxLength: OTP_LENGTH,
            }}
            disabled={busy}
            fullWidth
          />

          {error ? (
            <MDTypography variant="caption" color="error" display="block" mt={1}>
              {error}
            </MDTypography>
          ) : null}

          {secondsLeft != null ? (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              {expired
                ? 'Code expired. Tap Resend to get a new one.'
                : `Code expires in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`}
            </MDTypography>
          ) : null}
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        {onResend ? (
          <MDButton
            variant="text"
            color="info"
            onClick={onResend}
            disabled={busy}
            size="small"
          >
            Resend
          </MDButton>
        ) : (
          <span />
        )}
        <MDBox display="flex" gap={1}>
          {onClose ? (
            <MDButton
              variant="text"
              color="dark"
              onClick={onClose}
              disabled={busy}
              size="small"
            >
              Cancel
            </MDButton>
          ) : null}
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            disabled={busy || otp.length !== OTP_LENGTH || expired}
          >
            {busy ? <CircularProgress size={18} color="inherit" /> : 'Verify'}
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  )
}

OtpVerifyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  email: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  busy: PropTypes.bool,
  error: PropTypes.string,
  onVerify: PropTypes.func.isRequired,
  onResend: PropTypes.func,
  onClose: PropTypes.func,
  expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
}

OtpVerifyModal.defaultProps = {
  email: '',
  title: '',
  description: '',
  busy: false,
  error: '',
  onResend: null,
  onClose: null,
  expiresAt: null,
}

export default OtpVerifyModal
